import { FC, useContext, useMemo, useState } from 'react';

import { useMutation, useQuery } from '@apollo/client';
import {
  Button,
  ContextMenu,
  Select,
  Table,
  useContextMenu,
} from '@smartshell/ui-kit';

import { LayoutContext } from 'components/Layout';
import { LocalizationContext } from 'localization';

import { AchievementManager } from './AchievementManager';
import s from './Achievements.module.scss';
import { getConditionOptions, getRewardOptions } from './constants';
import {
  IDeleteAchievementRequest,
  IDeleteAchievementResponse,
  IFilters,
  IGetAchievementsResponse,
  TAchievement,
} from './interfaces';
import { DELETE_ACHIEVEMENT, GET_ACHIEVEMENTS } from './schema';
import { useContextMenuItems } from './useContextMenuItems';
import { UseGetColumns } from './UseGetColumns';

export const Achievements: FC = () => {
  const { t } = useContext(LocalizationContext);
  const layoutElement = useContext(LayoutContext);

  const [isEditMode, setIsEditMode] = useState(false);
  const [filters, setFilters] = useState<IFilters>({
    condition: null,
    reward: null,
  });

  const { data, loading } = useQuery<IGetAchievementsResponse>(
    GET_ACHIEVEMENTS,
    { fetchPolicy: 'cache-and-network' }
  );

  const [deleteAchievement] = useMutation<
    IDeleteAchievementResponse,
    IDeleteAchievementRequest
  >(DELETE_ACHIEVEMENT, {
    update: (cache, result) => {
      if (!result.data?.deleteAchievement) {
        return;
      }

      const deletedAchievement = result.data.deleteAchievement;
      cache.evict({
        id: cache.identify(deletedAchievement),
      });
      cache.gc();
    },
  });

  const filteredAchievements = useMemo(
    () =>
      data?.achievements.filter(
        (achievement) =>
          achievement.conditions.some((condition) =>
            filters.condition ? condition.name === filters.condition : true
          ) &&
          achievement.rewards.some((reward) =>
            filters.reward ? reward.name === filters.reward : true
          )
      ) ?? [],
    [filters, data?.achievements]
  );
  const COLUMNS = UseGetColumns();

  const {
    onContextMenu,
    contextMenuData,
    closeContextMenu,
    cursorPosition,
    onTouchMove,
    onTouchEnd,
    onTouchStart,
  } = useContextMenu<TAchievement>();

  const contextMenuItems = useContextMenuItems((variables) =>
    deleteAchievement({ variables })
  );

  return (
    <div className={s.container}>
      {isEditMode && (
        <AchievementManager
          close={() => {
            setIsEditMode(false);
          }}
        />
      )}
      <div className={s.controls}>
        <div className={s.filters}>
          <Select
            name="condition-filter"
            placeholder={t('achievements.condition.all')}
            options={getConditionOptions(t)}
            value={filters.condition}
            onChange={(value) =>
              setFilters((prevState) => ({ ...prevState, condition: value }))
            }
            errorWrapperClassName={s.conditions}
          />
          <Select
            name="rewards-filter"
            placeholder={t('achievements.reward.all')}
            options={getRewardOptions(t)}
            value={filters.reward}
            onChange={(value) =>
              setFilters((prevState) => ({ ...prevState, reward: value }))
            }
            errorWrapperClassName={s.rewards}
          />
        </div>
        <Button
          className={s.add}
          iconName="CirclePlusIcon"
          onClick={() => {
            setIsEditMode(true);
          }}
        >
          {t('achievement.add.button')}
        </Button>
      </div>
      <Table
        data={filteredAchievements}
        columns={COLUMNS}
        isLoading={loading}
        emptyDataPlaceholder={t(
          !data?.achievements.length
            ? 'achievements.placeholder'
            : 'achievements.placeholder.filtered'
        )}
        onContextMenu={(event, data) => {
          onContextMenu(event, data);
        }}
        onTouchStart={(event, row) => {
          onTouchStart(event, row);
        }}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        className={s.table}
      />
      {contextMenuData && cursorPosition && (
        <ContextMenu
          layoutElement={layoutElement}
          data={contextMenuData}
          items={contextMenuItems}
          closeContextMenu={closeContextMenu}
          position={cursorPosition}
        />
      )}
    </div>
  );
};
